import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { clearLabsDraft } from "@/features/labs/store";
import { ApiError } from "@/lib/api";
import { borrarBorradores } from "@/lib/borradores";
import { getUndoGeneration, resetUndo } from "@/lib/undo";

export function createQueryClient(): QueryClient {
  const mutationSessions = new WeakMap<object, number>();
  function expireSession(error: unknown) {
    if (!(error instanceof ApiError) || error.status !== 401) return;
    void client.cancelQueries({ queryKey: ["usuario"], exact: true });
    resetUndo();
    client.setQueryData(["usuario"], null);
    client.removeQueries({ predicate: query => query.queryKey[0] !== "usuario" && query.queryKey[0] !== "health" });
    clearLabsDraft();
    borrarBorradores();
  }
  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: expireSession,
      onSuccess: (data, query) => {
        if (query.queryKey[0] === "usuario" && data === null) {
          resetUndo();
          // Salud es pública y mantiene visible el modo incluso antes del ingreso.
          client.removeQueries({ predicate: cached => cached.queryKey[0] !== "usuario" && cached.queryKey[0] !== "health" });
          clearLabsDraft();
          borrarBorradores();
        }
      },
    }),
    mutationCache: new MutationCache({
      onMutate: (_variables, mutation) => { mutationSessions.set(mutation, getUndoGeneration()); },
      onError: (error, _variables, _context, mutation) => {
        if (mutationSessions.get(mutation) === getUndoGeneration()) expireSession(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        // Un 4xx no se arregla reintentando; un corte de red sí puede.
        retry: (failures, error) =>
          failures < 2 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
      },
    },
  });
  // T9 (tablero-velocidad-31oct V3): el tablero de Baldosa (hasta 184 KB) y los de Calados y Fundidos no se releen
  // cada vez que se vuelve a la ventana, solo si lo leído tiene más de un minuto. Un gesto los relee igual.
  for (const tablero of [["production", "board", "BA"], ["line-production", "CA", "board"], ["line-production", "FU", "board"]])
    client.setQueryDefaults(tablero, { refetchOnWindowFocus: (query) => Date.now() - query.state.dataUpdatedAt > TABLERO_AL_VOLVER_MS });
  return client;
}

/** Cuánto puede tener lo leído del tablero antes de releerlo al volver a la ventana. */
export const TABLERO_AL_VOLVER_MS = 60_000;
